(() => {
  const storageKey = 'vspots_favorites_v1';
  const recordsStorageKey = 'vspots_favorite_records_v1';
  const accountStorageKey = 'vspots_favorites_account_v1';
  const drawerSelector = '[data-vspots-favorites-drawer]';
  let hydrationRequest = null;
  let accountSyncRequest = null;

  function normalizeProductId(value) {
    return String(value || '').split('/').pop().trim();
  }

  function readFavorites() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(storageKey));
      return new Set(Array.isArray(stored) ? stored.map(normalizeProductId).filter(Boolean) : []);
    } catch {
      return new Set();
    }
  }

  function readRecords() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(recordsStorageKey));
      return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
    } catch {
      return {};
    }
  }

  function accountContext() {
    return {
      customerId: String(document.body.dataset.vspotsCustomerId || '').trim(),
      syncUrl: String(document.body.dataset.vspotsFavoritesSyncUrl || '').trim(),
    };
  }

  function prepareLocalAccountCache() {
    const { customerId } = accountContext();
    try {
      const cachedCustomerId = window.localStorage.getItem(accountStorageKey) || '';
      if (cachedCustomerId && cachedCustomerId !== customerId) {
        window.localStorage.removeItem(storageKey);
        window.localStorage.removeItem(recordsStorageKey);
        window.localStorage.removeItem(accountStorageKey);
      }
    } catch {
      return;
    }
  }

  prepareLocalAccountCache();

  let favorites = readFavorites();
  let records = readRecords();

  function labels() {
    return {
      add: document.body.dataset.vspotsFavoriteAddLabel || 'Als Favorit speichern',
      remove: document.body.dataset.vspotsFavoriteRemoveLabel || 'Aus Favoriten entfernen',
      saved: document.body.dataset.vspotsFavoriteSavedLabel || 'Gemerkt',
      save: document.body.dataset.vspotsFavoriteSaveLabel || 'Deal merken',
    };
  }

  function updateButton(button) {
    const productId = normalizeProductId(button.dataset.vspotsFavoriteProductId);
    const isFavorite = Boolean(productId && favorites.has(productId));
    const copy = labels();
    const visibleLabel = button.querySelector('[data-vspots-favorite-label]');

    button.setAttribute('aria-pressed', String(isFavorite));
    button.setAttribute('aria-label', isFavorite ? copy.remove : copy.add);
    button.title = isFavorite ? copy.remove : copy.add;
    if (visibleLabel) visibleLabel.textContent = isFavorite ? copy.saved : copy.save;
  }

  function updateButtons(root = document) {
    if (root instanceof Element && root.matches('[data-vspots-favorite-button]')) updateButton(root);
    root.querySelectorAll?.('[data-vspots-favorite-button]').forEach(updateButton);
  }

  function updateCounts() {
    document.querySelectorAll('[data-vspots-favorites-count]').forEach((count) => {
      count.textContent = String(favorites.size);
      count.hidden = favorites.size === 0;
    });

    const drawerCount = document.querySelector('[data-vspots-favorites-drawer-count]');
    if (drawerCount) {
      drawerCount.textContent = String(favorites.size);
      drawerCount.hidden = favorites.size === 0;
    }

    document.querySelectorAll('[data-vspots-favorites-trigger]').forEach((trigger) => {
      trigger.classList.toggle('has-favorites', favorites.size > 0);
    });
  }

  function saveState() {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([...favorites]));
      window.localStorage.setItem(recordsStorageKey, JSON.stringify(records));
    } catch {
      return;
    }
  }

  function applyFavoriteIds(productIds) {
    favorites = new Set(productIds.map(normalizeProductId).filter(Boolean));
    Object.keys(records).forEach((productId) => {
      if (!favorites.has(productId)) delete records[productId];
    });
    saveState();
    updateButtons();
    updateCounts();
    renderDrawer();
  }

  async function accountRequest(options = {}) {
    const { customerId, syncUrl } = accountContext();
    if (!customerId || !syncUrl) return null;
    const response = await window.fetch(syncUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', ...(options.headers || {}) },
    });
    if (!response.ok) throw new Error(`Favorites sync returned ${response.status}`);
    const payload = await response.json();
    return Array.isArray(payload.favorites) ? payload.favorites.map(normalizeProductId).filter(Boolean) : [];
  }

  async function synchronizeAccountFavorites({ mergeGuestFavorites = false } = {}) {
    const { customerId } = accountContext();
    if (!customerId) return false;
    if (accountSyncRequest) return accountSyncRequest;

    accountSyncRequest = (async () => {
      try {
        const cachedCustomerId = window.localStorage.getItem(accountStorageKey) || '';
        let remoteFavorites = await accountRequest();
        if (remoteFavorites === null) return false;

        if (mergeGuestFavorites && !cachedCustomerId && favorites.size > 0) {
          remoteFavorites = await accountRequest({
            body: JSON.stringify({ operation: 'merge', productIds: [...favorites] }),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });
        }

        window.localStorage.setItem(accountStorageKey, customerId);
        applyFavoriteIds(remoteFavorites || []);
        return true;
      } catch {
        return false;
      } finally {
        accountSyncRequest = null;
      }
    })();

    return accountSyncRequest;
  }

  async function persistAccountMutation(operation, productId) {
    const remoteFavorites = await accountRequest({
      body: JSON.stringify({ operation, productId }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });
    if (remoteFavorites !== null) applyFavoriteIds(remoteFavorites);
  }

  function firstText(root, selectors) {
    for (const selector of selectors) {
      const value = root?.querySelector(selector)?.textContent?.trim();
      if (value) return value;
    }
    return '';
  }

  function normalizeEuroPosition(value) {
    return String(value || '').replace(
      /€\s*((?:\d{1,3}(?:\.\d{3})*|\d+)(?:,\d{1,2})?)/gu,
      '$1\u202f€'
    );
  }

  function recordFromButton(button) {
    const productId = normalizeProductId(button.dataset.vspotsFavoriteProductId);
    if (!productId) return null;

    const card = button.closest('.vspots-deal-card, product-card, .product-information, main');
    const link = card?.querySelector('a[href*="/products/"]');
    const image = card?.querySelector('img');

    return {
      id: productId,
      title:
        button.dataset.vspotsFavoriteTitle ||
        firstText(card, ['.vspots-deal-card__body h3', '.vspots-card__body h3', 'h1', 'h2', 'h3']),
      url: button.dataset.vspotsFavoriteUrl || link?.getAttribute('href') || '',
      image: button.dataset.vspotsFavoriteImage || image?.currentSrc || image?.src || '',
      price:
        normalizeEuroPosition(
          button.dataset.vspotsFavoritePrice ||
            firstText(card, ['.vspots-deal-card__price strong', '.vspots-card__price strong', '[data-product-price]'])
        ),
      comparePrice:
        normalizeEuroPosition(
          button.dataset.vspotsFavoriteComparePrice ||
            firstText(card, ['.vspots-deal-card__price s', '.vspots-card__price s'])
        ),
      vendor:
        button.dataset.vspotsFavoriteVendor ||
        firstText(card, ['.vspots-deal-card__partner', '.vspots-card__partner']),
    };
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format((Number(cents) || 0) / 100);
  }

  function recordFromDeal(deal) {
    return {
      id: normalizeProductId(deal.shopify_product_id),
      title: String(deal.title || ''),
      url: String(deal.product_path || ''),
      image: String(deal.image_url || ''),
      price: `${Number(deal.max_price_cents) > Number(deal.min_price_cents) ? 'ab ' : ''}${formatMoney(
        deal.min_price_cents
      )}`,
      comparePrice:
        Number((deal.display_compare_at_price_cents ?? (Number(deal.variant_count) === 1 ? deal.max_compare_at_price_cents : 0))) > Number(deal.min_price_cents)
          ? formatMoney((deal.display_compare_at_price_cents ?? (Number(deal.variant_count) === 1 ? deal.max_compare_at_price_cents : 0)))
          : '',
      vendor: String(deal.vendor || ''),
    };
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function createFavoriteCard(productId, record, drawer) {
    const card = createElement('article', 'vspots-favorites-card');
    const content = record?.url ? document.createElement('a') : document.createElement('div');
    content.className = 'vspots-favorites-card__content';
    if (record?.url) content.href = record.url;

    const media = createElement('div', 'vspots-favorites-card__media');
    if (record?.image) {
      const image = document.createElement('img');
      image.src = record.image;
      image.alt = record.title || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    } else {
      media.append(createElement('span', 'vspots-favorites-card__placeholder', 'V'));
    }

    const body = createElement('div', 'vspots-favorites-card__body');
    body.append(
      createElement(
        'h3',
        '',
        record?.title || drawer.dataset.unavailableLabel || 'Dieser Deal ist aktuell nicht verfügbar.'
      )
    );
    if (record?.vendor) body.append(createElement('p', 'vspots-favorites-card__vendor', record.vendor));
    if (record?.price) {
      const price = createElement('div', 'vspots-favorites-card__price');
      if (record.comparePrice) price.append(createElement('s', '', normalizeEuroPosition(record.comparePrice)));
      price.append(createElement('strong', '', normalizeEuroPosition(record.price)));
      body.append(price);
    }
    content.append(media, body);

    const remove = createElement('button', 'vspots-favorites-card__remove');
    remove.type = 'button';
    remove.dataset.vspotsFavoriteRemove = productId;
    remove.setAttribute('aria-label', drawer.dataset.removeLabel || 'Aus Favoriten entfernen');
    remove.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-8-4.9-8-11.2C4 6.6 6.2 4.5 9 4.5c1.5 0 2.6.7 3 1.5.4-.8 1.5-1.5 3-1.5 2.8 0 5 2.1 5 5.3C20 16.1 12 21 12 21Z" fill="currentColor"/></svg>';
    card.append(content, remove);
    return card;
  }

  function setStatus(drawer, text = '') {
    const status = drawer.querySelector('[data-vspots-favorites-status]');
    if (!status) return;
    status.textContent = text;
    status.hidden = !text;
  }

  function renderDrawer({ hydrate = true } = {}) {
    const drawer = document.querySelector(drawerSelector);
    const list = drawer?.querySelector('[data-vspots-favorites-list]');
    const empty = drawer?.querySelector('[data-vspots-favorites-empty]');
    if (!drawer || !list || !empty) return;

    const productIds = [...favorites];
    list.replaceChildren(...productIds.map((productId) => createFavoriteCard(productId, records[productId], drawer)));
    list.hidden = productIds.length === 0;
    empty.hidden = productIds.length !== 0;
    if (productIds.length === 0) setStatus(drawer);

    const missingIds = productIds.filter((productId) => !records[productId]?.title);
    if (hydrate && missingIds.length > 0) hydrateMissingRecords(drawer, missingIds);
  }

  async function fetchDealsPage(drawer, page) {
    const baseUrl = String(drawer.dataset.searchApiUrl || '').trim();
    const url = new URL('v1/deals/search', baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`);
    url.searchParams.set('q', '');
    url.searchParams.set('page', String(page));
    url.searchParams.set('perPage', '100');
    url.searchParams.set('sort', 'relevance');
    const response = await window.fetch(url, { headers: { Accept: 'application/json' }, mode: 'cors' });
    if (!response.ok) throw new Error(`Favorites catalog request returned ${response.status}`);
    return response.json();
  }

  async function hydrateMissingRecords(drawer, missingIds) {
    if (hydrationRequest) return hydrationRequest;
    const targetIds = new Set(missingIds);
    setStatus(drawer, drawer.dataset.loadingLabel || 'Deine gemerkten Deals werden geladen…');

    hydrationRequest = (async () => {
      try {
        const firstPage = await fetchDealsPage(drawer, 1);
        const pages = Math.max(1, Math.ceil(Number(firstPage.found || 0) / Number(firstPage.per_page || 100)));
        const remainingPages = Array.from({ length: pages - 1 }, (_, index) => index + 2);
        const responses = [firstPage, ...(await Promise.all(remainingPages.map((page) => fetchDealsPage(drawer, page))))];

        responses.forEach((response) => {
          response.results?.forEach(({ deal }) => {
            const record = recordFromDeal(deal || {});
            if (targetIds.has(record.id)) records[record.id] = record;
          });
        });
        saveState();
        setStatus(drawer);
      } catch {
        setStatus(drawer, drawer.dataset.errorLabel || 'Einige gemerkte Deals konnten gerade nicht geladen werden.');
      } finally {
        hydrationRequest = null;
        renderDrawer({ hydrate: false });
      }
    })();

    return hydrationRequest;
  }

  function broadcastChange(productId) {
    document.dispatchEvent(
      new CustomEvent('vspots:favorites-change', {
        detail: { count: favorites.size, isFavorite: favorites.has(productId), productId },
      })
    );
  }

  async function removeFavorite(productId) {
    await accountSyncRequest;
    const previousFavorites = new Set(favorites);
    const previousRecord = records[productId];
    favorites.delete(productId);
    delete records[productId];
    saveState();
    updateButtons();
    updateCounts();
    renderDrawer();
    broadcastChange(productId);
    if (!accountContext().customerId) return;
    try {
      await persistAccountMutation('remove', productId);
    } catch {
      favorites = previousFavorites;
      if (previousRecord) records[productId] = previousRecord;
      saveState();
      updateButtons();
      updateCounts();
      renderDrawer();
    }
  }

  async function openDrawer(trigger) {
    const menuDetails = trigger.closest('details[open]');
    if (menuDetails) menuDetails.open = false;

    renderDrawer();
    await window.customElements.whenDefined('theme-drawer');
    document.querySelector('#favorites-drawer')?.open();
    void synchronizeAccountFavorites().then(() => renderDrawer());
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('[data-vspots-favorites-trigger]');
    if (trigger instanceof HTMLButtonElement) {
      event.preventDefault();
      openDrawer(trigger);
      return;
    }

    const remove = target?.closest('[data-vspots-favorite-remove]');
    if (remove instanceof HTMLButtonElement) {
      event.preventDefault();
      void removeFavorite(normalizeProductId(remove.dataset.vspotsFavoriteRemove));
      return;
    }

    const button = target?.closest('[data-vspots-favorite-button]');
    if (!(button instanceof HTMLButtonElement)) return;

    const productId = normalizeProductId(button.dataset.vspotsFavoriteProductId);
    if (!productId) return;

    event.preventDefault();
    event.stopPropagation();
    void (async () => {
      await accountSyncRequest;
      const previousFavorites = new Set(favorites);
      const previousRecord = records[productId];
      const operation = favorites.has(productId) ? 'remove' : 'add';
      if (operation === 'remove') {
        favorites.delete(productId);
        delete records[productId];
      } else {
        favorites.add(productId);
        const record = recordFromButton(button);
        if (record) records[productId] = record;
      }
      saveState();
      updateButtons();
      updateCounts();
      renderDrawer();
      broadcastChange(productId);

      if (!accountContext().customerId) return;
      try {
        await persistAccountMutation(operation, productId);
      } catch {
        favorites = previousFavorites;
        if (previousRecord) records[productId] = previousRecord;
        else delete records[productId];
        saveState();
        updateButtons();
        updateCounts();
        renderDrawer();
      }
    })();
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey && event.key !== recordsStorageKey) return;
    favorites = readFavorites();
    records = readRecords();
    updateButtons();
    updateCounts();
    renderDrawer({ hydrate: false });
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) updateButtons(node);
      });
    });
  });

  const initialize = () => {
    updateButtons();
    updateCounts();
    renderDrawer({ hydrate: false });
    observer.observe(document.body, { childList: true, subtree: true });
    void synchronizeAccountFavorites({ mergeGuestFavorites: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
